/**
 * Remote backend — wraps the live y-partyserver + DraftService stack + the
 * typed oRPC API client.
 *
 * Architect review [A-M1]: `Backend` now exposes the full CRUD surface
 * through `backend.api.<entity>.<method>()`. The remote implementation
 * delegates each method to the typed client helpers in
 * `packages/client-core/src/api/*.ts` — those wrappers are the authoritative
 * shape; this file is a dispatch layer.
 *
 * The commit path still goes through a transient WebSocket to the sync
 * Worker; that part is unchanged from Track 2.L / IW.3.
 *
 * Phase 2 review fix S-M1 / A-M3:
 *   The obs-log commit envelope no longer carries `branchUuid` or
 *   `frozenBodyMarkdown`. The sync Worker resolves both server-side via
 *   `/internal/resolve-commit-context`; sending them was invariant #9 noise.
 *
 * Spec refs:
 *   Track 2.L — "UI code calls the same interfaces either way"
 *   architecture.md §4.22 (commit protocol), §4.4 (y-doc lifecycle)
 *   architecture.md §4.23 — server-stamped author_agent
 */

import { Doc } from "yjs";
import { WebsocketProvider } from "y-partyserver/provider";
import { buildYDocKey, type YDocKey, parseYDocKey } from "../sync/index.js";
import { createApiClient, type ApiClient } from "../api/index.js";
import type {
  ArtefactKindCreateInput,
  ArtefactKindRow,
  ArtefactKindUpdateInput,
} from "../api/artefact-kinds.js";
import {
  createArtefactKind as apiCreateArtefactKind,
  deleteArtefactKind as apiDeleteArtefactKind,
  getArtefactKind as apiGetArtefactKind,
  listArtefactKinds as apiListArtefactKinds,
  updateArtefactKind as apiUpdateArtefactKind,
} from "../api/artefact-kinds.js";
import type {
  ArtefactCreateInput,
  ArtefactListInput,
  ArtefactObsBodyRow,
  ArtefactRow,
  ArtefactSlugHistoryEntry,
  ArtefactUpdateInput,
} from "../api/artefacts.js";
import {
  createArtefact as apiCreateArtefact,
  deleteArtefact as apiDeleteArtefact,
  getArtefact as apiGetArtefact,
  getArtefactObsBody as apiGetArtefactObsBody,
  listArtefacts as apiListArtefacts,
  listArtefactSlugHistory as apiListArtefactSlugHistory,
  updateArtefact as apiUpdateArtefact,
} from "../api/artefacts.js";
import type { AttemptDto, ListAttemptsParams } from "../api/attempts.js";
import type {
  BatchCloseInput,
  BatchCreateInput,
  BatchListInput,
  BatchRow,
  BatchUpdateInput,
} from "../api/batches.js";
import {
  archiveBatch as apiArchiveBatch,
  closeBatch as apiCloseBatch,
  createBatch as apiCreateBatch,
  deleteBatch as apiDeleteBatch,
  getBatch as apiGetBatch,
  listBatches as apiListBatches,
  reopenBatch as apiReopenBatch,
  updateBatch as apiUpdateBatch,
} from "../api/batches.js";
import type { BranchDto, CreateBranchParams, PromoteBranchParams } from "../api/branches.js";
import { createCollectionsClient } from "../api/collections.js";
import type { CollectionDto, CollectionMemberDto } from "../api/collections.js";
import { createLibraryConfigClient } from "../api/library-config.js";
import {
  createRatingSchemaVersion as apiCreateRatingSchemaVersion,
  getRatingSchemaVersion as apiGetRatingSchemaVersion,
  listRatingSchemaVersions as apiListRatingSchemaVersions,
} from "../api/rating-schemas.js";
import type { RatingSchemaVersionRecord } from "../api/rating-schemas.js";
import type {
  RecipeCreateInput,
  RecipeListInput,
  RecipePromoteInput,
  RecipeUpdateInput,
} from "../api/recipes.js";
import {
  createRecipe as apiCreateRecipe,
  deleteRecipe as apiDeleteRecipe,
  getRecipe as apiGetRecipe,
  listRecipes as apiListRecipes,
  listRecipeSlugHistory as apiListRecipeSlugHistory,
  promoteRecipe as apiPromoteRecipe,
  updateRecipe as apiUpdateRecipe,
  spinOffRecipe as apiSpinOffRecipe,
  setRecipeFeatureMedia as apiSetRecipeFeatureMedia,
} from "../api/recipes.js";
import type {
  Backend,
  BackendApi,
  BackendMode,
  CommitAttemptInput,
  CommitAttemptResult,
  SeedDraftFromFrozenInput,
  SeedDraftFromFrozenResult,
  LibraryConfigPutInput,
  LibraryConfigResult,
  RatingSchemaCreateInput,
  SearchQueryInput,
  SearchResponse,
} from "./types.js";

export interface RemoteBackendOptions {
  /** oRPC API origin, e.g. `https://proofcook.com`. */
  apiBaseUrl: string;
  /** y-partyserver origin (usually same as apiBaseUrl). */
  syncBaseUrl: string;
  /**
   * Path prefix where y-partyserver is mounted on the sync Worker.
   * Defaults to `/sync` — matches apps/sync/src/index.ts.
   */
  syncPath?: string;
  /**
   * Override the global WebSocket — useful for tests that inject a mock WS
   * implementation (e.g. `mock-socket`).
   */
  WebSocketPolyfill?: typeof WebSocket;
  /**
   * Maximum time to wait for a commit-ack after the WS is open (ms).
   * Default 15 s (DEFAULT_COMMIT_TIMEOUT_MS). A healthy commit returns in
   * <1 s; a server taking 15 s is broken, and surfacing the failure sooner is
   * better UX than the previous 30 s wall-clock hang.
   *
   * Note: WS connection failures (401/403/network drop) reject sooner than
   * this — see MAX_CONNECTION_FAILURES in sendCommit. The timeout is the
   * backstop for the "WS open but server never ack'd" case.
   */
  commitTimeoutMs?: number;
  /**
   * Inject a pre-built `ApiClient` (tests mock the whole CRUD surface this
   * way). When omitted, a client is constructed via `createApiClient` against
   * `apiBaseUrl`.
   */
  apiClient?: ApiClient;
  /** Override the global fetch used by the oRPC client. */
  fetch?: typeof fetch;
}

// ── Wire types (must match apps/sync/src/commit/index.ts) ────────────────────

/**
 * Phase 2 review fix S-M1 / A-M3:
 *   The envelope no longer carries `branchUuid` or `frozenBodyMarkdown`.
 *   For obs-* commits the sync Worker resolves both server-side via the
 *   `/internal/resolve-commit-context` RPC on apps/api. Sending those fields
 *   today is logged + ignored server-side; a future release may reject.
 */
interface CommitMessageWire {
  type: "commit";
  /** Wave 3 R-2: only "cooked" is valid now that "in-progress" is removed. */
  targetStatus: "cooked";
  ratingSchemaVersionUuid?: string | null;
  batchUuid?: string | null;
  /**
   * SERVER IGNORES this field — included here only to exercise the
   * "client-supplied author_agent is logged and discarded" path if a future
   * test wants to cover it. The server uses the session-derived agent.
   */
  authorAgent?: string;
}

interface CommitAckWire {
  type: "commit-ack";
  attemptUuid: string;
  /** Wave 3 R-2: always null now that "in-progress" is removed. */
  obsLogDocKey: string | null;
  targetStatus: "cooked";
}

interface CommitErrorWire {
  type: "commit-error";
  reason: string;
  message?: string;
}

type CommitReplyWire = CommitAckWire | CommitErrorWire;

// ── Seed-a-draft-from-a-frozen-version wire (A-WP10) ──────────────────────────

interface SeedDraftMessageWire {
  type: "seed-draft-from-frozen";
  sourceAttemptUuid: string;
  confirmReplace?: boolean;
}

interface SeedDraftAckWire {
  type: "seed-draft-from-frozen-ack";
  blockCount: number;
  seededFromAttemptUuid: string;
}

interface SeedDraftNeedsConfirmWire {
  type: "seed-draft-needs-confirm";
  sourceAttemptUuid: string;
}

interface SeedDraftErrorWire {
  type: "seed-draft-from-frozen-error";
  reason: string;
  message?: string;
}

/**
 * All replies the sync-DO can send for a seed message: the three seed-specific
 * envelopes PLUS the generic `commit-error` raised by sync-do.ts validation
 * before the handler runs (`wrong_doc_kind`, `missing_source_attempt`,
 * `internal_error`). The client MUST handle `commit-error` or those failures are
 * silently dropped (a Rule 8 hole — noted in the seam contract).
 */
type SeedDraftReplyWire =
  SeedDraftAckWire | SeedDraftNeedsConfirmWire | SeedDraftErrorWire | CommitErrorWire;

// ── CRUD surface (oRPC delegations) ───────────────────────────────────────────

/**
 * Build the `BackendApi` shape from a typed oRPC `ApiClient`.
 *
 * Each namespace delegates to the corresponding helper module in
 * `packages/client-core/src/api/*.ts` — those modules are the single source
 * of shape truth for the remote backend. This factory is just a thin dispatch
 * layer that glues the helpers together under the unified `BackendApi`.
 */
function buildRemoteBackendApi(api: ApiClient): BackendApi {
  // Sub-clients that wrap oRPC dynamic-proxy calls in typed methods.
  // The structural-interface trick keeps them insulated from the oRPC router
  // type issues — see each module's header comment for context.
  const collectionsClient = createCollectionsClient(
    api as unknown as Parameters<typeof createCollectionsClient>[0],
  );
  const libraryConfigClient = createLibraryConfigClient(
    api as unknown as Parameters<typeof createLibraryConfigClient>[0],
  );

  // Dynamic-proxy accessors for the groups that don't have a structural
  // wrapper. Casting to `any` here is safe because the call signatures are
  // already typed at the public `BackendApi` boundary.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const branchApi = (api as any).branch as {
    list(input: { recipeUuid: string }): Promise<unknown>;
    get(input: { uuid: string }): Promise<unknown>;
    create(input: unknown): Promise<unknown>;
    archive(input: { uuid: string }): Promise<unknown>;
    promote?(input: unknown): Promise<unknown>;
    rename?(input: { branchUuid: string; newName: string }): Promise<unknown>;
    delete?(input: { branchUuid: string }): Promise<unknown>;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const versionApiRaw = (api as any).version as
    | {
        promote?(input: { attemptUuid: string }): Promise<unknown>;
        saveAsVersion?(input: { attemptUuid: string }): Promise<unknown>;
        discardDraft?(input: { attemptUuid: string }): Promise<unknown>;
        amendRecord?(input: {
          recipeUuid: string;
          variationUuid: string;
          attemptUuid: string;
          rating?: number;
          note?: string;
          // A-WP5: this-version hypothesis appended as `@intent` (frozen-body has
          // none). Three-state post message (absent=unchanged, null=delete,
          // string=replace) — cook-event metadata on the attempt row (B-WP1a).
          intent?: string;
          postMessage?: string | null;
        }): Promise<unknown>;
        // A-WP8: owner label on a frozen version.
        setLabel?(input: {
          recipeUuid: string;
          variationUuid: string;
          attemptUuid: string;
          label: string | null;
        }): Promise<unknown>;
      }
    | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const attemptApi = (api as any).attempt as {
    list(input: ListAttemptsParams): Promise<unknown>;
    get(input: { uuid: string }): Promise<unknown>;
    ensureDraft(input: { branchUuid: string }): Promise<unknown>;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const searchApi = (api as any).search as {
    query(input: SearchQueryInput): Promise<unknown>;
  };

  return {
    recipe: {
      list: (input?: RecipeListInput) => apiListRecipes(api, input ?? {}),
      get: (uuid: string) => apiGetRecipe(api, uuid),
      create: (input: RecipeCreateInput) => apiCreateRecipe(api, input),
      update: (input: RecipeUpdateInput) => apiUpdateRecipe(api, input),
      delete: (uuid: string) => apiDeleteRecipe(api, uuid),
      promote: (input: RecipePromoteInput) => apiPromoteRecipe(api, input),
      slugHistory: (recipeUuid: string) => apiListRecipeSlugHistory(api, recipeUuid),
      spinOff: (input: { sourceRecipeUuid: string } | { sourcePublicUuid: string }) =>
        apiSpinOffRecipe(api, input),
      setFeatureMedia: (input: { recipeUuid: string; mediaUuid: string | null }) =>
        apiSetRecipeFeatureMedia(api, input),
    },

    branch: {
      async list(recipeUuid: string): Promise<{ branches: BranchDto[] }> {
        const res = await branchApi.list({ recipeUuid });
        return res as { branches: BranchDto[] };
      },
      async get(uuid: string): Promise<BranchDto> {
        const res = await branchApi.get({ uuid });
        return res as BranchDto;
      },
      async create(input: CreateBranchParams): Promise<BranchDto> {
        const res = await branchApi.create(input);
        return res as BranchDto;
      },
      async archive(uuid: string): Promise<{ ok: boolean }> {
        const res = await branchApi.archive({ uuid });
        return res as { ok: boolean };
      },
      async promote(input: PromoteBranchParams): Promise<{ ok: boolean }> {
        if (typeof branchApi.promote !== "function") {
          // Server-side not yet wired at MVP (branch.promote is in the follow-up
          // track). Recipe-level promote is the primary path; see
          // `recipe.promote` above which updates the two-tier go-to pointers
          // (recipe.defaultVariationUuid + branch.promotedAttemptUuid).
          throw new Error(
            "backend.branch.promote: not implemented on remote backend (pending server wire-in). Use backend.recipe.promote for the canonical go-to pointer.",
          );
        }
        const res = await branchApi.promote(input);
        return res as { ok: boolean };
      },
      async rename(branchUuid: string, newName: string): Promise<{ ok: boolean }> {
        if (typeof branchApi.rename !== "function") {
          throw new Error("backend.branch.rename: not available on this backend.");
        }
        const res = await branchApi.rename({ branchUuid, newName });
        return res as { ok: boolean };
      },
      async delete(branchUuid: string): Promise<{ ok: boolean }> {
        if (typeof branchApi.delete !== "function") {
          throw new Error("backend.branch.delete: not available on this backend.");
        }
        const res = await branchApi.delete({ branchUuid });
        return res as { ok: boolean };
      },
    },

    version: {
      async promote(attemptUuid: string): Promise<{
        ok: boolean;
        attemptUuid: string;
        recipeUuid: string;
        previousPromotedUuid: string | null;
      }> {
        const promote = versionApiRaw?.promote;
        if (typeof promote !== "function") {
          throw new Error(
            "backend.version.promote: not available on this backend (Wave 2 Package A pending).",
          );
        }
        const res = await promote({ attemptUuid });
        return res as {
          ok: boolean;
          attemptUuid: string;
          recipeUuid: string;
          previousPromotedUuid: string | null;
        };
      },

      /**
       * Wave 3 R-2 — atomic freeze-draft-and-open-fresh mutation.
       * Replaces the old `markComplete` + `startNext` two-call chain.
       * Q2 semantics: no outcome precondition, no carriedItems.
       */
      async saveAsVersion(input: { attemptUuid: string }): Promise<{
        ok: boolean;
        newAttemptUuid: string;
        frozenAttemptUuid?: string;
        branchUuid: string;
      }> {
        const saveAsVersion = versionApiRaw?.saveAsVersion;
        if (typeof saveAsVersion !== "function") {
          throw new Error("backend.version.saveAsVersion: not available on this backend.");
        }
        const res = await saveAsVersion(input);
        // NEW-BUG-1: the server now returns frozenAttemptUuid (the just-frozen
        // version). Passed straight through so the post-freeze prompt amends the
        // right version instead of relying on stale client state.
        return res as {
          ok: boolean;
          newAttemptUuid: string;
          frozenAttemptUuid?: string;
          branchUuid: string;
        };
      },

      /**
       * Wave 5 — discard the current draft and revert to the last saved version.
       * Deletes the draft y-doc + attempt row; re-points HEAD to the previous
       * saved version. Irreversible.
       */
      async discardDraft(input: { attemptUuid: string }): Promise<{
        ok: boolean;
      }> {
        const discardDraft = versionApiRaw?.discardDraft;
        if (typeof discardDraft !== "function") {
          throw new Error(
            "backend.version.discardDraft: not yet available on this backend (Wave 5 server endpoint pending).",
          );
        }
        const res = await discardDraft(input);
        return res as { ok: boolean };
      },

      /**
       * Cook-closure D1/A2 — append an outcome annotation to a frozen
       * version's record (`@rating` / `@notes` only; procedure text stays
       * immutable). Stamps `post_close_edited_at` server-side.
       */
      async amendRecord(input: {
        recipeUuid: string;
        variationUuid: string;
        attemptUuid: string;
        rating?: number;
        note?: string;
        intent?: string;
        postMessage?: string | null;
      }): Promise<{ ok: true; editedAt: string }> {
        const amendRecord = versionApiRaw?.amendRecord;
        if (typeof amendRecord !== "function") {
          throw new Error("backend.version.amendRecord: not available on this backend.");
        }
        const res = await amendRecord(input);
        return res as { ok: true; editedAt: string };
      },
      async setLabel(input: {
        recipeUuid: string;
        variationUuid: string;
        attemptUuid: string;
        label: string | null;
      }): Promise<{ attemptUuid: string; label: string | null }> {
        const setLabel = versionApiRaw?.setLabel;
        if (typeof setLabel !== "function") {
          throw new Error("backend.version.setLabel: not available on this backend.");
        }
        const res = await setLabel(input);
        return res as { attemptUuid: string; label: string | null };
      },
    },

    attempt: {
      async list(
        input: ListAttemptsParams,
      ): Promise<{ attempts: AttemptDto[]; nextCursor: string | null }> {
        const res = await attemptApi.list(input);
        return res as { attempts: AttemptDto[]; nextCursor: string | null };
      },
      async get(uuid: string): Promise<AttemptDto> {
        const res = await attemptApi.get({ uuid });
        return res as AttemptDto;
      },
      async ensureDraft(input: { branchUuid: string }): Promise<{
        ok: true;
        attemptUuid: string;
        created: boolean;
        bodyMarkdown: string;
      }> {
        const res = await attemptApi.ensureDraft(input);
        return res as { ok: true; attemptUuid: string; created: boolean; bodyMarkdown: string };
      },
    },

    collection: {
      list: () => collectionsClient.listCollections(),
      create: (name: string) => collectionsClient.createCollection(name),
      rename: (uuid: string, name: string) => collectionsClient.renameCollection(uuid, name),
      delete: (uuid: string) => collectionsClient.deleteCollection(uuid),
      addMembership: (collectionUuid: string, recipeUuid: string) =>
        collectionsClient.addMembership(collectionUuid, recipeUuid),
      removeMembership: (collectionUuid: string, recipeUuid: string) =>
        collectionsClient.removeMembership(collectionUuid, recipeUuid),
      listRecipesInCollection: (collectionUuid: string) =>
        collectionsClient.listRecipesInCollection(collectionUuid),
      listCollectionsForRecipe: (recipeUuid: string) =>
        collectionsClient.listCollectionsForRecipe(recipeUuid),
      // Perf plan §2.C — one batched read of every membership (was N× listMembers).
      listAllMembers: () => collectionsClient.listAllMemberships(),
    } satisfies BackendApi["collection"] & {
      list(): Promise<{ collections: CollectionDto[] }>;
      listRecipesInCollection(collectionUuid: string): Promise<{ members: CollectionMemberDto[] }>;
    },

    ratingSchema: {
      async list(recipeUuid: string): Promise<{ versions: RatingSchemaVersionRecord[] }> {
        const res = await apiListRatingSchemaVersions(api, recipeUuid);
        return res as { versions: RatingSchemaVersionRecord[] };
      },
      async get(versionUuid: string): Promise<RatingSchemaVersionRecord> {
        const res = await apiGetRatingSchemaVersion(api, versionUuid);
        return res as RatingSchemaVersionRecord;
      },
      async create(input: RatingSchemaCreateInput): Promise<RatingSchemaVersionRecord> {
        const res = await apiCreateRatingSchemaVersion(api, input.recipeUuid, input.content);
        return res as RatingSchemaVersionRecord;
      },
    },

    artefactKind: {
      list: () => apiListArtefactKinds(api),
      get: (uuid: string) => apiGetArtefactKind(api, uuid),
      create: (input: ArtefactKindCreateInput) => apiCreateArtefactKind(api, input),
      update: (input: ArtefactKindUpdateInput) => apiUpdateArtefactKind(api, input),
      delete: (uuid: string) => apiDeleteArtefactKind(api, uuid),
    } satisfies {
      list(): Promise<{ kinds: ArtefactKindRow[] }>;
      get(uuid: string): Promise<ArtefactKindRow>;
      create(input: ArtefactKindCreateInput): Promise<ArtefactKindRow>;
      update(input: ArtefactKindUpdateInput): Promise<ArtefactKindRow>;
      delete(uuid: string): Promise<{ ok: boolean }>;
    },

    artefact: {
      list: (input?: ArtefactListInput) => apiListArtefacts(api, input ?? {}),
      get: (uuid: string) => apiGetArtefact(api, uuid),
      create: (input: ArtefactCreateInput) => apiCreateArtefact(api, input),
      update: (input: ArtefactUpdateInput) => apiUpdateArtefact(api, input),
      delete: (uuid: string) => apiDeleteArtefact(api, uuid),
      slugHistory: (uuid: string) => apiListArtefactSlugHistory(api, uuid),
      getObsBody: (uuid: string) => apiGetArtefactObsBody(api, uuid),
    } satisfies {
      list(
        input?: ArtefactListInput,
      ): Promise<{ artefacts: ArtefactRow[]; nextCursor: string | null }>;
      get(uuid: string): Promise<ArtefactRow>;
      create(input: ArtefactCreateInput): Promise<ArtefactRow>;
      update(input: ArtefactUpdateInput): Promise<ArtefactRow>;
      delete(uuid: string): Promise<{ ok: boolean }>;
      slugHistory(uuid: string): Promise<{ history: ArtefactSlugHistoryEntry[] }>;
      getObsBody(uuid: string): Promise<ArtefactObsBodyRow>;
    },

    batch: {
      list: (input?: BatchListInput) => apiListBatches(api, input ?? {}),
      get: (uuid: string) => apiGetBatch(api, uuid),
      create: (input: BatchCreateInput) => apiCreateBatch(api, input),
      update: (input: BatchUpdateInput) => apiUpdateBatch(api, input),
      close: (input: BatchCloseInput) => apiCloseBatch(api, input),
      reopen: (uuid: string) => apiReopenBatch(api, uuid),
      archive: (uuid: string) => apiArchiveBatch(api, uuid),
      delete: (uuid: string) => apiDeleteBatch(api, uuid),
    } satisfies {
      list(input?: BatchListInput): Promise<{ batches: BatchRow[]; nextCursor: string | null }>;
      get(uuid: string): Promise<BatchRow>;
      create(input: BatchCreateInput): Promise<BatchRow>;
      update(input: BatchUpdateInput): Promise<BatchRow>;
      close(input: BatchCloseInput): Promise<BatchRow>;
      reopen(uuid: string): Promise<BatchRow>;
      archive(uuid: string): Promise<BatchRow>;
      delete(uuid: string): Promise<{ ok: boolean }>;
    },

    libraryConfig: {
      async get(): Promise<LibraryConfigResult> {
        return libraryConfigClient.getLibraryConfig();
      },
      async put(input: LibraryConfigPutInput): Promise<LibraryConfigResult> {
        return libraryConfigClient.putLibraryConfig(input.configYaml);
      },
    },

    search: {
      async query(input: SearchQueryInput): Promise<SearchResponse> {
        const res = await searchApi.query(input);
        return res as SearchResponse;
      },
    },
  };
}

// ── Factory ───────────────────────────────────────────────────────────────────

export function createRemoteBackend(opts: RemoteBackendOptions): Backend {
  const timeoutMs = opts.commitTimeoutMs ?? DEFAULT_COMMIT_TIMEOUT_MS;

  const apiClient =
    opts.apiClient ??
    createApiClient({
      baseUrl: opts.apiBaseUrl,
      ...(opts.fetch !== undefined ? { fetch: opts.fetch } : {}),
    });

  const api = buildRemoteBackendApi(apiClient);

  return {
    mode: "remote" satisfies BackendMode,

    api,

    async commitAttempt(input: CommitAttemptInput): Promise<CommitAttemptResult> {
      // Wave 3 R-2: only the draft → cooked path remains.
      // The obs-log (in-progress) path was removed with the 5→4 state collapse.
      // The doc key is always the draft:<branchUuid> key.
      const docKey = buildYDocKey("draft", input.branchUuid);
      const payload: CommitMessageWire = {
        type: "commit",
        targetStatus: input.targetStatus,
        ratingSchemaVersionUuid: input.ratingSchemaVersionUuid ?? null,
        batchUuid: input.batchUuid ?? null,
      };

      // Open a transient WS provider, send the commit envelope, await the ack.
      // We do NOT hold a long-lived WS for commit dispatch — the commit is a
      // short-lived RPC-over-WS call. The editor holds its own provider for
      // CRDT sync; that provider is independent of this one.
      return await sendCommit(opts, docKey, payload, timeoutMs);
    },

    async seedDraftFromFrozen(input: SeedDraftFromFrozenInput): Promise<SeedDraftFromFrozenResult> {
      // A-WP10 ("Start a draft from vN"): replace the live draft body with a
      // frozen version's canonical markdown. Dispatched over a transient WS to
      // the SAME draft:<branchUuid> DO the editor's live provider is bound to,
      // so the server-side replace propagates to the editor as one update frame.
      const docKey = buildYDocKey("draft", input.branchUuid);
      const payload: SeedDraftMessageWire = {
        type: "seed-draft-from-frozen",
        sourceAttemptUuid: input.sourceAttemptUuid,
        ...(input.confirmReplace ? { confirmReplace: true } : {}),
      };
      return await sendSeedDraftFromFrozen(opts, docKey, payload, timeoutMs);
    },
  };
}

// ── WS commit transport ───────────────────────────────────────────────────────

/**
 * How many WS connection attempts are allowed to fail before we surface the
 * failure to the caller. The y-partyserver provider auto-reconnects with
 * exponential backoff on every `close`/`error`. Without a fail-fast cap, a WS
 * upgrade that's denied (401/403) or a network drop would cause the caller to
 * sit on the 30 s wall-clock timeout with no feedback — which is the exact
 * "Logging attempt times out" symptom reported on dev (2026-05-03).
 *
 * Two close events is the threshold: the first close may be a transient
 * disconnect mid-handshake; a second close inside the same commit attempt
 * means the connection is genuinely failing and we should reject so the
 * caller can show a brand-voice error.
 */
const MAX_CONNECTION_FAILURES = 2;

/**
 * Wall-clock timeout for the whole commit RPC. Shorter than the previous 30 s
 * default — at MVP a healthy commit returns in <1 s, and a server that takes
 * 15 s is broken. Surfacing the failure sooner is better UX than a 30-second
 * hang. The `fail-fast on connection failures` path above usually fires first
 * anyway; this is a backstop for the case where the WS is open but the server
 * never replies (e.g. a wedged DraftService).
 */
const DEFAULT_COMMIT_TIMEOUT_MS = 15_000;

/**
 * Open a provider for the given doc key, send a custom commit message, and
 * resolve with the parsed commit-ack (or reject on commit-error / timeout /
 * WS connection failure).
 *
 * The provider is torn down in the finally block so the WS does not leak.
 *
 * Failure surface (Rule 8):
 *   - commit-error reply → reject with the server-supplied reason
 *   - WS connection-error / repeated close → reject with `commit_ws_error`
 *   - Wall-clock timeout → reject with `commit_timeout`
 *
 * Each of these is caught by the SpeedLog / FullModal handler and surfaced as
 * a brand-voice toast / inline error. The previous shape silently waited the
 * full 30 s on any WS failure with no diagnostic, which violated Rule 8.
 */
async function sendCommit(
  opts: RemoteBackendOptions,
  key: YDocKey,
  payload: CommitMessageWire,
  timeoutMs: number,
): Promise<CommitAttemptResult> {
  if (!parseYDocKey(key)) {
    throw new Error(`remoteBackend: invalid y-doc key ${key}`);
  }

  const baseUrl = opts.syncBaseUrl.replace(/\/+$/, "");
  const syncPath = (opts.syncPath ?? "/sync").replace(/^\/?/, "/").replace(/\/+$/, "");
  const wsBase = baseUrl.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");
  const url = `${wsBase}${syncPath}/${encodeURIComponent(key)}/ws`;

  const doc = new Doc();
  const providerOpts: NonNullable<ConstructorParameters<typeof WebsocketProvider>[3]> = {
    connect: true,
    isPrefixedUrl: true,
  };
  if (opts.WebSocketPolyfill !== undefined) {
    providerOpts.WebSocketPolyfill = opts.WebSocketPolyfill;
  }
  const provider = new WebsocketProvider(url, key, doc, providerOpts);

  try {
    const reply = await new Promise<CommitReplyWire>((resolve, reject) => {
      let settled = false;
      let connectionFailures = 0;
      let envelopeSent = false;

      const cleanup = () => {
        settled = true;
        provider.off("custom-message", onCustomMessage);
        provider.off("connection-error", onConnectionError);
        provider.off("connection-close", onConnectionClose);
        provider.off("status", onStatus);
        clearTimeout(timer);
      };

      const rejectWithCode = (code: string, detail: string) => {
        if (settled) return;
        cleanup();
        // The error.message is what the SpeedLog/FullModal `catch` reads via
        // `err.message` and renders as the inline error (after a brand-voice
        // wrapper at the call site). Format: `<code>: <detail>` so a future
        // toast layer can branch on the code prefix.
        reject(new Error(`${code}: ${detail}`));
      };

      const onCustomMessage = (msg: string) => {
        if (settled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(msg);
        } catch {
          return; // not a JSON envelope; keep listening
        }
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          typeof (parsed as { type?: unknown }).type !== "string"
        ) {
          return;
        }
        const type = (parsed as { type: string }).type;
        if (type === "commit-ack" || type === "commit-error") {
          cleanup();
          resolve(parsed as CommitReplyWire);
        }
      };

      // y-partyserver emits `connection-error` when the WebSocket fires its
      // `error` event (network drop, refused upgrade, TLS failure). The
      // provider then enters a backoff loop and tries to reconnect.
      const onConnectionError = (event: unknown) => {
        if (settled) return;
        connectionFailures += 1;
        if (connectionFailures >= MAX_CONNECTION_FAILURES) {
          // String-coerce the event lazily — most browser WS error events are
          // bare Event objects with no useful diagnostic, so the code is what
          // the caller surfaces.
          rejectWithCode("commit_ws_error", `connection failed (${connectionFailures} attempts)`);
        }
        // Suppress unused-arg warning while keeping the event in scope for
        // future diagnostics (e.g. logging the close-code).
        void event;
      };

      // `connection-close` fires after `error` (or on a clean close). If we
      // never made it to OPEN before close, this is the right place to fail
      // — without this guard, a 401/403 upgrade response leaves the caller
      // sitting on the wall-clock timeout with no signal.
      const onConnectionClose = (event: unknown) => {
        if (settled) return;
        // If the envelope already went out and we're waiting for the ack, a
        // close is unrecoverable: the DO won't send the ack on a closed WS,
        // and the reconnected WS would need a fresh `commit` round-trip.
        if (envelopeSent) {
          rejectWithCode("commit_ws_closed", "connection closed before ack arrived");
          return;
        }
        connectionFailures += 1;
        if (connectionFailures >= MAX_CONNECTION_FAILURES) {
          rejectWithCode(
            "commit_ws_error",
            `connection closed before ready (${connectionFailures} attempts)`,
          );
        }
        void event;
      };

      const timer = setTimeout(() => {
        if (settled) return;
        rejectWithCode("commit_timeout", `no ack within ${timeoutMs} ms`);
      }, timeoutMs);

      provider.on("custom-message", onCustomMessage);
      provider.on("connection-error", onConnectionError);
      provider.on("connection-close", onConnectionClose);

      // Send the commit envelope once the WS is open. If the WS is not yet
      // open, we wait for the `status` observable to emit `connected`.
      const trySend = () => {
        const ws = provider.ws;
        if (ws && ws.readyState === /* OPEN */ 1) {
          ws.send(`__YPS:${JSON.stringify(payload)}`);
          envelopeSent = true;
          return true;
        }
        return false;
      };

      const onStatus = (e: unknown) => {
        if (
          typeof e === "object" &&
          e !== null &&
          (e as { status?: string }).status === "connected"
        ) {
          if (trySend()) {
            provider.off("status", onStatus);
          }
        }
      };

      if (!trySend()) {
        provider.on("status", onStatus);
      }
    });

    if (reply.type === "commit-error") {
      throw new Error(`commit_error: ${reply.reason}${reply.message ? `: ${reply.message}` : ""}`);
    }

    return {
      ok: true,
      attemptUuid: reply.attemptUuid,
      obsLogDocKey: reply.obsLogDocKey,
    };
  } finally {
    try {
      provider.destroy();
    } catch {
      /* best-effort */
    }
    try {
      doc.destroy();
    } catch {
      /* best-effort */
    }
  }
}

/**
 * Open a transient provider for the draft doc key, send a seed-draft-from-frozen
 * message, and resolve with the ack / needs-confirm result (or reject on
 * seed-error / commit-error / timeout / WS failure).
 *
 * Mirrors {@link sendCommit}'s connection lifecycle. Failure surface (Rule 8):
 *   - seed-draft-from-frozen-error / commit-error reply → reject with the reason
 *   - WS connection-error / repeated close → reject with `seed_ws_error`
 *   - Wall-clock timeout → reject with `seed_timeout`
 * The caller maps the thrown code to brand-voice copy; the `message` field is
 * diagnostic-only and never rendered verbatim.
 */
async function sendSeedDraftFromFrozen(
  opts: RemoteBackendOptions,
  key: YDocKey,
  payload: SeedDraftMessageWire,
  timeoutMs: number,
): Promise<SeedDraftFromFrozenResult> {
  if (!parseYDocKey(key)) {
    throw new Error(`remoteBackend: invalid y-doc key ${key}`);
  }

  const baseUrl = opts.syncBaseUrl.replace(/\/+$/, "");
  const syncPath = (opts.syncPath ?? "/sync").replace(/^\/?/, "/").replace(/\/+$/, "");
  const wsBase = baseUrl.replace(/^https:\/\//, "wss://").replace(/^http:\/\//, "ws://");
  const url = `${wsBase}${syncPath}/${encodeURIComponent(key)}/ws`;

  const doc = new Doc();
  const providerOpts: NonNullable<ConstructorParameters<typeof WebsocketProvider>[3]> = {
    connect: true,
    isPrefixedUrl: true,
  };
  if (opts.WebSocketPolyfill !== undefined) {
    providerOpts.WebSocketPolyfill = opts.WebSocketPolyfill;
  }
  const provider = new WebsocketProvider(url, key, doc, providerOpts);

  try {
    const reply = await new Promise<SeedDraftReplyWire>((resolve, reject) => {
      let settled = false;
      let connectionFailures = 0;
      let envelopeSent = false;

      const cleanup = () => {
        settled = true;
        provider.off("custom-message", onCustomMessage);
        provider.off("connection-error", onConnectionError);
        provider.off("connection-close", onConnectionClose);
        provider.off("status", onStatus);
        clearTimeout(timer);
      };

      const rejectWithCode = (code: string, detail: string) => {
        if (settled) return;
        cleanup();
        reject(new Error(`${code}: ${detail}`));
      };

      const onCustomMessage = (msg: string) => {
        if (settled) return;
        let parsed: unknown;
        try {
          parsed = JSON.parse(msg);
        } catch {
          return; // not a JSON envelope; keep listening
        }
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          typeof (parsed as { type?: unknown }).type !== "string"
        ) {
          return;
        }
        const type = (parsed as { type: string }).type;
        if (
          type === "seed-draft-from-frozen-ack" ||
          type === "seed-draft-needs-confirm" ||
          type === "seed-draft-from-frozen-error" ||
          type === "commit-error"
        ) {
          cleanup();
          resolve(parsed as SeedDraftReplyWire);
        }
      };

      const onConnectionError = (event: unknown) => {
        if (settled) return;
        connectionFailures += 1;
        if (connectionFailures >= MAX_CONNECTION_FAILURES) {
          rejectWithCode("seed_ws_error", `connection failed (${connectionFailures} attempts)`);
        }
        void event;
      };

      const onConnectionClose = (event: unknown) => {
        if (settled) return;
        if (envelopeSent) {
          rejectWithCode("seed_ws_closed", "connection closed before ack arrived");
          return;
        }
        connectionFailures += 1;
        if (connectionFailures >= MAX_CONNECTION_FAILURES) {
          rejectWithCode(
            "seed_ws_error",
            `connection closed before ready (${connectionFailures} attempts)`,
          );
        }
        void event;
      };

      const timer = setTimeout(() => {
        if (settled) return;
        rejectWithCode("seed_timeout", `no reply within ${timeoutMs} ms`);
      }, timeoutMs);

      provider.on("custom-message", onCustomMessage);
      provider.on("connection-error", onConnectionError);
      provider.on("connection-close", onConnectionClose);

      const trySend = () => {
        const ws = provider.ws;
        if (ws && ws.readyState === /* OPEN */ 1) {
          ws.send(`__YPS:${JSON.stringify(payload)}`);
          envelopeSent = true;
          return true;
        }
        return false;
      };

      const onStatus = (e: unknown) => {
        if (
          typeof e === "object" &&
          e !== null &&
          (e as { status?: string }).status === "connected"
        ) {
          if (trySend()) {
            provider.off("status", onStatus);
          }
        }
      };

      if (!trySend()) {
        provider.on("status", onStatus);
      }
    });

    if (reply.type === "seed-draft-from-frozen-error" || reply.type === "commit-error") {
      throw new Error(`seed_error: ${reply.reason}${reply.message ? `: ${reply.message}` : ""}`);
    }
    if (reply.type === "seed-draft-needs-confirm") {
      return { status: "needs-confirm", sourceAttemptUuid: reply.sourceAttemptUuid };
    }
    return {
      status: "ack",
      blockCount: reply.blockCount,
      seededFromAttemptUuid: reply.seededFromAttemptUuid,
    };
  } finally {
    try {
      provider.destroy();
    } catch {
      /* best-effort */
    }
    try {
      doc.destroy();
    } catch {
      /* best-effort */
    }
  }
}
